import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {
  ApiKey, AwsIoTMQTTAccessDetails,
  BaseStation, CA,
  Channel,
  CombinedReading,
  Device, DeviceConfiguration,
  DeviceConfigurationSet,
  Enterprise,
  Group,
  Info, IoTCoreCert,
  LoraParser,
  Message,
  Session,
  User
} from '@/types/core';
import {HttpResponse} from '@/types/http';
import {Delivery, PushMessage, Subscription} from '@/types/core-push';
import * as _ from 'lodash';
import {GetEnterpriseQuery} from "@/services/EnterpriseService";
import {GroupConfigurationParameter, GroupConfigurationQuery, GroupConfigurationRequest} from "@/services/GroupService";
import moment from "moment";

export class CoreClient {

  private coreBaseURL: string = process.env.VUE_APP_CORE_API_BASE_URL!;
  
  private currentSession:Session|null = null;

  private baseConfig: any = {
    timeout: 40000,
    validateStatus: () => true,
    headers: {}
  };

  public async doLogin(email: string, password?: string): Promise<LoginReply & HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/login`,
      data: {
        email,
        password,
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, session: resp.data as Session};
  }

  public async postAuthCode(token: string, authCode: number): Promise<HttpResponse> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/login/auth-code`,
      data: {
        token,
        authCode,
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public doLogout(): void {
    this.clearSession();
  }

  public getCurrentSession():Session|null {

    if(!this.currentSession)
      return null;

    return Object.assign({}, this.currentSession);
  }

  public async getDevices(req: GetDevicesReq): Promise<GetDevicesReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices`,
      params: {
        skip: req.skip,
        limit: req.limit,
        _ts: new Date().getTime(),
        network: true,
        nwServer: false,
        parserData: false,
        customMeta: true,
        eventList: false
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, deviceList: resp.data as Device[]};
  }

  public async getDevicesInGroup(req: GetDevicesInGroupReq): Promise<GetDevicesReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices`,
      params: {
        skip: req.skip,
        limit: req.limit,
        groupIdList: req.groupIdList,
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, deviceList: resp.data as Device[]};
  }

  public async searchDevices(req: SearchDevicesReq): Promise<SearchDeviceReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/devices/search`,
      data: {
        limit: req.limit,
        customMeta: req.customMeta,
        query: req.query
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, deviceList: resp.data as Device[]};
  }

  public async getDevice(req: GetDeviceReq): Promise<GetDeviceReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices/${req.deviceId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, device: resp.data as Device};
  }

  public async loadDeviceConfiguration(req: GetDeviceReq): Promise<LoadDeviceConfigurationReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/configuration`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      configuration: resp.data as DeviceConfigurationSet};
  }

  public async loadGroupConfiguration(req: GroupConfigurationQuery): Promise<LoadConfigurationReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/groups/${req.groupId}/configuration`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    if (!resp) {
      Object.assign(resp, {
        httpCode: 500,
        errorMsg: 'Unable to fetch group configuration',
        data: {
          configuration: []
        }
      });
    }

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      configuration: resp.data.configuration as GroupConfigurationParameter[]};
  }

  public async sendDeviceCommand(req: DeviceCommandReq): Promise<DeviceDownlinksQueueReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/downlinks/queue`,
      params: {
        _ts: new Date().getTime(),
      },
      data: req,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      queue: resp.data.queue as DownlinkItem[]};
  }

  public async sendGroupCommand(req: GroupCommandReq): Promise<DeviceDownlinksQueueReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/groups/${req.groupId}/downlink`,
      params: {
        _ts: new Date().getTime(),
      },
      data: req,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      queue: resp.data ? resp.data.queue as DownlinkItem[] : []};
  }

  public async requestGroupConfiguration(req: GroupConfigurationRequest): Promise<DeviceDownlinksQueueReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const data:DeviceCommand = {
      command: 'request_configuration',
    }

    const params = _.map(req.params, (param) => {
      return {id: param};
    });

    if (params.length > 0) {
      data.params = params as DeviceCommandParams[];
    }

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/groups/${req.groupId}/downlink`,
      params: {
        _ts: new Date().getTime(),
      },
      data,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      queue: resp.data ? resp.data.queue as DownlinkItem[] : []};
  }

  public async fetchGroupDownlinkQueue(req: GroupConfigurationQuery): Promise<DeviceDownlinksQueueReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/groups/${req.groupId}/downlink`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      queue: resp.data ? resp.data.queue as DownlinkItem[] : []};
  }

  public async requestDeviceConfiguration(req: DeviceConfigurationReq): Promise<DeviceDownlinksQueueReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const data: any = {
      command: 'request_configuration',
    };

    if (req.params) {
      const params = _.map(req.params, (param) => {
        return {id: param};
      });
      data.params = params;
    }
    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/downlinks/queue`,
      params: {
        _ts: new Date().getTime(),
      },
      data,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      queue: resp.data ? resp.data.queue as DownlinkItem[] : []};
  }

  public async getDownlinksQueue(req: GetDeviceReq): Promise<DeviceDownlinksQueueReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/downlinks/queue`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      queue: resp.data.queue as DownlinkItem[]};
  }

  public async updateDeviceName(req: UpdateDeviceNameReq): Promise<DeviceUpdateResponse & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/name`,
      data: {
        name: req.name
      },
    });

    const resp = await this.doRequest(cfg, 200);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, tpwSync: resp.data?.tpwSync};
  }

  public async updateDeviceType(req: UpdateDeviceTypeReq): Promise<HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/type`,
      data: {
        deviceType: req.deviceType
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async updateDeviceLoraParser(req: UpdateDeviceLoraParserReq): Promise<HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/lora-parser`,
      data: {
        loraParserId: req.loraParserId
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async updateDeviceMetadata(req: UpdateDeviceMetadataReq): Promise<HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/custom-metadata`,
      data: req.customMeta
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async updateBaseStationMetadata(req: UpdateBaseStationMetadataReq): Promise<HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/base-stations/${req.lrrId}/custom-metadata`,
      data: req.customMeta
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async getInfo(): Promise<GetInfoReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/info`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, info: resp.data as Info};
  }

  public async searchGroups(req: SearchGroupsReq): Promise<SearchGroupReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/groups/search`,
      data: {
        limit: req.limit,
        query: req.query
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, groupList: resp.data as Group[]};
  }

  public async getEnterprise(req: GetEntReq): Promise<GetEntReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/enterprises/${req.enterpriseId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, enterprise: resp.data as Enterprise};
  }

  public async getEnterprisesAndGroups(query: GetEnterpriseQuery): Promise<GetEnteprisesReply & HttpResponse> {
    // we restore the currently stored session
    this.restoreSession();

    const params:any = {
      offset: query.pageNumber * query.pageSize,
      limit: query.pageSize,
      _ts: new Date().getTime(),
    };

    if ( query.filter ) {
      params.filter = query.filter;
    }

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/enterprises`,
      params: params,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      enterprises: resp.data.enterprises,
      totalNumberOfPages: resp.data.totalNumberOfPages};
  }

  public async getUsers(req: GetUsersReq): Promise<GetUsersReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/users`,
      params: {
        skip: req.skip,
        limit: req.limit,
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, userList: resp.data as User[]};
  }

  public async getApiKeys(req: GetApiKeysReq): Promise<GetApiKeysReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/api-keys`,
      params: {
        skip: req.skip,
        limit: req.limit,
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, apiKeyList: resp.data as ApiKey[]};
  }

  
  public async createUser(req: CreateUserReq): Promise<CreateUserReply & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/users`,
      data: {
        email: req.email,
        groupIdList: req.groupIdList,
        level: req.level,
        password: req.password,
        validFrom: req.validFrom,
        validTo: req.validTo
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, user: resp.data as User };
  }

  public async updateUser(req: UpdateUserReq): Promise<UpdateUserReply & HttpResponse> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/users/${req.email}`,
      data: {
        groupIdList: req.groupIdList,
        status: req.status,
        level: req.level,
        password: req.password,
        validFrom: req.validFrom,
        validTo: req.validTo
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, user: resp.data as User };
  }

  public async deleteUser(req: DeleteUserReq): Promise<HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.coreBaseURL}/users/${req.email}`,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async createApiKey(req: CreateApiKeyReq): Promise<CreateApiKeyReply & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/api-keys`,
      data: {
        tps: req.tps,
        tpd: req.tpd,
        level: req.level,
        groupIdList: req.groupIdList,
        description: req.description
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, apiKey: resp.data as ApiKey };
  }

  public async updateApiKey(req: UpdateApiKeyReq): Promise<HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/api-keys/`,
      data: {
        apiKey: req.apiKey,
        tps: req.tps,
        tpd: req.tpd,
        level: req.level,
        status: req.status,
        groupIdList: req.groupIdList,
        description: req.description
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg };
  }

  public async deleteApiKey(req: DeleteApiKeyReq): Promise<HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.coreBaseURL}/api-keys/`,
      data: req,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async createMQTTCert(apiKeyId:string): Promise<AwsIoTMQTTAccessDetails & HttpReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/api-keys/mqtt`,
      data: {
        apiKey: apiKeyId,
      }
    });

    const resp = await this.doRequest(cfg, 200);

    const awsIoTMQTTAccessDetails: AwsIoTMQTTAccessDetails = resp.data as AwsIoTMQTTAccessDetails;

    return _.assign({}, {httpCode: resp.httpCode, errorMsg: resp.errorMsg}, awsIoTMQTTAccessDetails);
  }

  public async deactivateMQTTCert(apiKeyId:string, mqttCert: IoTCoreCert): Promise<HttpReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/api-keys/mqtt/${mqttCert.certId}/deactivate`,
      data: {
        apiKey: apiKeyId,
      }
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async activateMQTTCert(apiKeyId:string, mqttCert: IoTCoreCert): Promise<HttpReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/api-keys/mqtt/${mqttCert.certId}/activate`,
      data: {
        apiKey: apiKeyId,
      }
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async deleteMQTTCert(apiKeyId:string, mqttCert: IoTCoreCert): Promise<HttpReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.coreBaseURL}/api-keys/mqtt/${mqttCert.certId}`,
      data: {
        apiKey: apiKeyId,
      }
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async getMqttCA(): Promise<HttpReply & CA> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/api-keys/mqtt-ca.pem`,
    });

    const resp = await this.doRequest(cfg, 200);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, pem: resp.data};
  }

  public async updateDeviceLocation(req:UpdateDeviceLocReq):Promise<DeviceUpdateResponse & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/location`,
      data: {
        latitude: req.latitude,
        longitude: req.longitude,
      },
    });

    const resp = await this.doRequest(cfg, 200);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, tpwSync: resp.data?.tpwSync};
  }

  public async updateDeviceGroups(req:UpdateDeviceGroupsReq):Promise<DeviceUpdateResponse & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/groups`,
      data: {
        groupIdList: req.groupIdList
      },
    });

    const resp = await this.doRequest(cfg, 200);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, tpwSync: resp.data?.tpwSync};
  }

  public async getMessages(req:DeviceMessagesReq):Promise<DeviceMessagesReply & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/messages`,
      params: {
        fromTs: req.fromTs,
        toTs: req.toTs,
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, messageList: resp.data as Message[]};
  }

  public async getLatestMessages(req:LatestDeviceMessagesReq):Promise<DeviceMessagesReply & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/latest-messages`,
      params: {
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, messageList: resp.data as Message[]};
  }

  public async postMessages(req:NewMessageReq):Promise<HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/messages`,
      data: {
        hexPayload: req.hexPayload,
        loraPort: req.loraPort,
        downlinkCounter: req.downlinkCounter,
        flushQueue: req.flushQueue,
        validityTime: req.validityTime
      },
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async loadBaseStation(lrrId:string):Promise<HttpReply & GetBaseStationReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/base-stations/${lrrId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, baseStation: resp.data as BaseStation};
  }

  public async getBaseStations(req:GetBaseStationsReq):Promise<HttpReply & GetBaseStationsReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/base-stations`,
      params: {
        limit: req.limit,
        skip: req.skip,
        enterpriseIdList: req.enterpriseIdList
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, bssList: resp.data as BaseStation[]};
  }

  public async getBaseStationTrafficTimeSeries(req:GetBaseStationTrafficTimeSeriesReq):Promise<HttpReply & GetBaseStationTrafficTimeSeriesReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/base-stations/${req.lrrId}/traffic/${req.timeType}/timeseries`,
      params: {
        start: req.start,
        finish: req.finish,
        interval: req.interval,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, timeSeries: resp.data.timeSeries as TrafficStatsTimeSeriesPoint[]};
  }

  public async getBaseStationUniqueDevicesTimeSeries(req:GetBaseStationTrafficTimeSeriesReq):Promise<HttpReply & GetBaseStationUniqueDevicesTimeSeriesReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/base-stations/${req.lrrId}/devices/${req.timeType}/timeseries`,
      params: {
        start: req.start,
        finish: req.finish,
        interval: req.interval,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, timeSeries: resp.data.timeSeries as UniqueDevicesTimeSeriesPoint[]};
  }

  public async getBaseStationMeanPERTimeSeries(req:GetBaseStationTrafficTimeSeriesReq):Promise<HttpReply & GetBaseStationMeanPERTimeSeriesReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/base-stations/${req.lrrId}/per/${req.timeType}/timeseries`,
      params: {
        start: req.start,
        finish: req.finish,
        interval: req.interval,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, timeSeries: resp.data.timeSeries as MeanPERTimeSeriesPoint[]};
  }

  public async getBaseStationCnxTimeSeries(req:GetBaseStationTrafficTimeSeriesReq):Promise<HttpReply & GetBaseStationCnxStatusTimeSeriesReply> {
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/base-stations/${req.lrrId}/status/timeseries`,
      params: {
        start: req.start,
        finish: req.finish,
        interval: req.interval,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, timeSeries: resp.data.timeSeries as CnxTimeSeriesPoint[]};
  }

  public async getSubscriptions(req:{skip:number, limit:number}):Promise<{subList?:Subscription[]} & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/subscriptions`,
      params: {
        skip: req.skip,
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, subList: resp.data as Subscription[]};
  }

  public async getSubscription(req:{subId:number}):Promise<{sub?:Subscription} & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/subscriptions/${req.subId}`,
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, sub: resp.data as Subscription};
  }

  public async getSubDeliveries(req:{subId:number, limit:number}):Promise<{deliveryList?:Delivery[]} & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/subscriptions/${req.subId}/deliveries`,
      params: {
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, deliveryList: resp.data as Delivery[]};
  }

  public async getSubPending(req:{subId:number, limit:number}):Promise<{pendingList?:PushMessage[]} & HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/subscriptions/${req.subId}/pending`,
      params: {
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, pendingList: resp.data as PushMessage[]};
  }

  public async createSub(req:{enterpriseId:string, type:string, endpoint:object, 
    deviceType?:string, pushInterval:number, cascade:boolean, format:string}):Promise<{newSub?:Subscription} & HttpReply> {
    
      const cfg = Object.assign({}, this.baseConfig, {
        method: 'post',
        url: `${this.coreBaseURL}/subscriptions`,
        data: req
      });
  
      const resp = await this.doRequest(cfg);
  
      return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, newSub: resp.data as Subscription };
  }

  public async deleteSub(req:{subId:number}): Promise<HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.coreBaseURL}/subscriptions/${req.subId}`,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public async enrolDevice(req:EnrolDevReq):Promise<{newDevice?:Device} & HttpReply> {
    
    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/devices`,
      data: req
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, newDevice: resp.data as Device};
  }

  public async unenrolDevice(req:{deviceId:string}): Promise<HttpReply> {

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.coreBaseURL}/devices/${req.deviceId}`,
    });

    const resp = await this.doRequest(cfg, 204);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg};
  }

  public isLoggedIn(): boolean {
    return this.baseConfig.headers.authorization !== undefined;
  }

  public async getReadings(req: GetReadingsReq): Promise<HttpResponse & GetReadingsReply> {

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/devices/${req.deviceId}/readings`,
      params: {
        fromTs: req.fromTs,
        toTs: req.toTs,
        limit: req.limit,
      }
    });

    const resp = await this.doRequest(cfg);

    return {
      httpCode: resp.httpCode,
      errorMsg: resp.errorMsg,
      readingList: resp.data as CombinedReading[]
    };
  }

  //TODO
  public async getLoraParsers(req: GetLoraParsersReq): Promise<GetLoraParserReply & HttpResponse>{

    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/lora-parsers`,
      params: {
        skip: req.skip,
        limit: req.limit,
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);
    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, loraParserList: resp.data as LoraParser[]};
  }

  public async getLoraParser(req: GetLoraParserReq): Promise<GetLoraParserReply & HttpResponse>{
    
    // we restore the currently stored session
    this.restoreSession();

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'get',
      url: `${this.coreBaseURL}/lora-parsers/${req.loraParserId}`,
      params: {
        _ts: new Date().getTime(),
      },
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, loraParser: resp.data as LoraParser};

  }

  public async createLoraParser(req: CreateLoraParserReq): Promise<GetLoraParserReply & HttpResponse>{

    this.restoreSession()

    console.log(req)

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/lora-parsers`,
      data: req
    });

    const resp = await this.doRequest(cfg);

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, loraParser: resp.data as LoraParser};

  }

  public async updateLoraParser(req: UpdateLoraParserReq): Promise<HttpResponse> {

    this.restoreSession()

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'put',
      url: `${this.coreBaseURL}/lora-parsers/${req.loraParserId}`,
      data: {
        codeJs: req.codeJs,
        name: req.name
      }
    });

    const resp = await this.doRequest(cfg, 204)

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg}

  }

  public async testLoraParser(req: TestLoraParserReq): Promise<TestLoraParserReply & HttpResponse>{

    this.restoreSession()

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'post',
      url: `${this.coreBaseURL}/lora-parsers/${req.loraParserId}/test`,
      data: {
        hexPayload: req.hexPayload,
        loraPort: req.loraPort
      }
    });

    const resp = await this.doRequest(cfg)

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg, parsePayloadReply: resp.data as ParsePayloadReply}
  }

  public async deleteLoraParser(req: DeleteLoraParserReq):Promise<HttpResponse>{

    this.restoreSession()

    const cfg = Object.assign({}, this.baseConfig, {
      method: 'delete',
      url: `${this.coreBaseURL}/lora-parsers/${req.loraParserId}`,
    });

    const resp = await this.doRequest(cfg, 204)

    return {httpCode: resp.httpCode, errorMsg: resp.errorMsg}

  }

  private async doRequest(req: AxiosRequestConfig, expectedHttpCode?: number): Promise<HttpReply> {

    let resp: AxiosResponse;

    try {
      resp = await axios.request(req);
    } catch (err) {
      return {errorMsg: `${err}`};
    }

    // we expect a code 200 by default
    expectedHttpCode = expectedHttpCode !== undefined ? expectedHttpCode : 200;

    if (resp.status !== expectedHttpCode) {
      if (resp.data && resp.data.message) {
        return {httpCode: resp.status, errorMsg: `${resp.data.message} (HTTP ${resp.status})`};
      } else {
        return {httpCode: resp.status, errorMsg: `unexpected HTTP error ${resp.status}`};
      }
    }
    return Promise.resolve({httpCode: resp.status, data: resp.data});
  }

  
  public setSession(session:Session) {
    this.currentSession = session;
    localStorage.setItem('v3-nnnco-session', JSON.stringify(session));
    this.baseConfig.headers = {authorization: `Bearer ${session.token}`};
  }

  private restoreSession() {
    const ret = localStorage.getItem('v3-nnnco-session');

    if(!ret)
      return;

    let parsed: Session | undefined;
    try {
      parsed = JSON.parse(ret) as Session;
    } catch (err) { 
      this.clearSession();
      return {};
    }

    // if we don't have a session or if it has expired let's clear the session
    if(!parsed.token || (parsed.expiresAt * 1000) < new Date().getTime())
      return this.clearSession();

    this.currentSession = parsed;

    // we restore the session
    this.baseConfig.headers = {authorization: `Bearer ${parsed.token}`};
  }

  private clearSession() {
    this.baseConfig.headers = {};
    this.currentSession = null;
    localStorage.removeItem('v3-nnnco-session');
  }

}

export interface GetDevicesReq {
  skip: number;
  limit: number;
}

export interface GetDevicesInGroupReq {
  skip: number;
  limit: number;
  groupIdList: string;
}

export interface SearchDevicesReq {
  query?: string;
  customMeta?: Record<string, string|number|boolean|string[]|number[]>;
  limit: number;
}

export interface GetDeviceReq {
  deviceId: string;
}

export interface DeviceCommandParams {
  id: number;
  value: string | number;
  ignoreUpdateConfiguration?: boolean;
}

export interface DeviceCommand {
  command: string;
  params?: DeviceCommandParams[];
}

export interface DeviceCommandReq {
  deviceId: string;
  command: string;
  params?: DeviceCommandParams[];
}

export interface GroupCommandReq {
  groupId: string;
  command: string;
  params?: DeviceCommandParams[];
}

export interface DeviceConfigurationReq {
  deviceId: string;
  params?: number[];
}

export interface UpdateDeviceNameReq {
  deviceId: string;
  name: string;
}

export interface UpdateDeviceTypeReq {
  deviceId: string;
  deviceType?: string;
}

export interface UpdateDeviceLoraParserReq {
  deviceId: string;
  loraParserId?: string;
}

export interface UpdateDeviceMetadataReq {
  deviceId: string;
  customMeta: Record<string, string|number|boolean|string[]|number[]>;
}

export interface UpdateBaseStationMetadataReq {
  lrrId: string;
  customMeta: Record<string, string|number|boolean|string[]|number[]>;
}

export interface DeleteUserReq {
  email: string;
}

export interface UpdateApiKeyReq {
  apiKey: string;
  tps: number;
  tpd: number;
  level: string;
  status: string;
  groupIdList: string[];
  description?: string;
}

export interface NewMessageReq {
  deviceId: string;
  hexPayload: string;
  loraPort: number;
  downlinkCounter?: number;
  flushQueue?:boolean;
  validityTime?:number;
}

export interface UpdateDeviceLocReq {
  deviceId:string;
  latitude:number;
  longitude:number;
}

export interface UpdateDeviceGroupsReq {
  deviceId:string;
  groupIdList:string[];
}

export interface SearchGroupsReq {
  query?: string;
  limit: number;
}

export interface HttpReply {
  httpCode?: number;
  errorMsg?: string;
  data?: any;
}

export interface LoginReply {
  session?: Session;
}

export interface GetDevicesReply {
  deviceList?: Device[];
}

export interface SearchDeviceReply {
  deviceList?: Device[];
}

export interface GetDeviceReply {
  device?: Device;
}

export interface LoadDeviceConfigurationReply {
  configuration: DeviceConfigurationSet;
}

export interface LoadConfigurationReply {
  configuration: GroupConfigurationParameter[];
}

export interface RawDownlink {
  hexPayload: string;
  loraPort: number;
}

export interface DownlinkItem {
  deviceId?: string;
  rawDownlink: RawDownlink;
  comparableHexPayload: string;
  createdAt: number;
  deviceCommand: DeviceCommand;
}

export interface DeviceDownlinksQueueReply {
  queue: DownlinkItem[];
}

export interface GetInfoReply {
  info?: Info;
}

// Get Enterprise
export interface GetEntReq {
  enterpriseId: string;
}

export interface GetEntReply {
  enterprise?: Enterprise;
}

export interface GetEnteprisesReply {
  totalNumberOfPages: number,
  enterprises: any;
}

// Get Users
export interface GetUsersReq {
  skip: number;
  limit: number;
}

export interface GetUsersReply {
  userList?: User[];
}

// Get API Keys
export interface GetApiKeysReq {
  skip: number;
  limit: number;
}

export interface GetApiKeysReply {
  apiKeyList?: ApiKey[];
}

// Update User Req
export interface UpdateUserReq {
  email: string;
  status?: string;
  level?: string;
  groupIdList?: string[];
  password?: string|null;
  validFrom?: number|null;
  validTo?: number|null;
}

export interface UpdateUserReply {
  user?: User;
}

// Device messages
export interface DeviceMessagesReq {
  deviceId:string;
  fromTs:number;
  toTs:number;
  limit:number;
}

export interface DeviceMessagesReply {
  messageList:Message[];
}

// Device latest messages

export interface LatestDeviceMessagesReq {
  deviceId:string;
  limit:number;
}

// Create user
export interface CreateUserReq {
  email: string;
  level: string;
  groupIdList: string[];
  password: string;
  validFrom?: number;
  validTo?: number;
}

export interface CreateUserReply {
  user?: User;
}

// Create API Key
export interface CreateApiKeyReq {
  tps: number;
  tpd: number;
  level: string;
  groupIdList: string[];
  description?: string;
}

export interface CreateApiKeyReply {
  apiKey?: ApiKey;
}

// Delete API key
export interface DeleteApiKeyReq {
  apiKey: string;
}

// Groups
export interface SearchGroupReply {
  groupList?: Group[];
}

// GetBaseStations

export interface GetBaseStationsReq {
  skip: number;
  limit: number;
  enterpriseIdList?: string[];
}

export interface GetBaseStationReply {
  baseStation: BaseStation;
}

export interface GetBaseStationsReply {
  bssList:BaseStation[];
}

// GetBaseStationTrafficTimeSeries

export interface GetBaseStationTrafficTimeSeriesReq {
  lrrId: string;
  start: number;
  finish: number;
  interval: number; // '1h' | '1d'
  timeType?: string; // 'gw' | 'ns'
}

export interface TrafficStatsTimeSeriesPoint {
  ts: number;
  uplinks: number;
  downlinks: number;
  total: number;
}

export interface GetBaseStationTrafficTimeSeriesReply {
  timeSeries?: TrafficStatsTimeSeriesPoint[];
}

export interface UniqueDevicesTimeSeriesPoint {
  ts: number,
  uniqueDevices: number;
}

export interface GetBaseStationUniqueDevicesTimeSeriesReply {
  timeSeries?: UniqueDevicesTimeSeriesPoint[];
}

export interface MeanPERTimeSeriesPoint {
  ts: number,
  meanPER: number;
}

export interface GetBaseStationMeanPERTimeSeriesReply {
  timeSeries?: MeanPERTimeSeriesPoint[];
}

export interface CnxTimeSeriesPoint {
  ts: number;
  onlineStatus: number;
}

export interface GetBaseStationCnxStatusTimeSeriesReply {
  timeSeries?: CnxTimeSeriesPoint[];
}

// Device Enrolment

export interface EnrolDevReq {
  deviceId: string;
  enterpriseId: string;
  deviceType?: string;
  name: string;
  enrolment: {
    mode:'otaa'|'abp',
    class:'A'|'C',
    otaa?: {
      appEui: string;
      appKey: string;
      nwAddress?: string;
    },
    abp?: {
      nwKey: string;
      nwAddress: string;
      appSKey?: string;
    }
  };
}

export interface GetReadingsReq {
  deviceId: string;
  channelId: number;
  fromTs: number;
  toTs: number;
  limit: number;
}

export interface GetReadingsReply {
  readingList?: CombinedReading[];
}

export interface GetLoraParserReq {
  loraParserId:string;
}

export interface GetLoraParserReply {
  loraParser?: LoraParser
}

export interface GetLoraParsersReq {
  skip: number;
  limit: number;
}

export interface GetLoraParserReply {
  loraParserList?: LoraParser[];
}

export interface CreateLoraParserReq {
  name: string
  enterpriseId: string
  codeJs: string
}

export interface  UpdateLoraParserReq {
  loraParserId: string
  codeJs?: string
  name?: string
}

export interface TestLoraParserReq {
  loraParserId: string
  hexPayload: string
  loraPort: number
}

export interface TestLoraParserReply{
  parsePayloadReply?: ParsePayloadReply
}

export interface ParsePayloadReply {
  errorMsg?: string;
  executionResult?: ExecutionResult
}

export interface ExecutionResult {
  durationMs: number;
  readingList: Channel[];
  consoleList: string[]
}

export interface DeleteLoraParserReq {
  loraParserId: string,
}

// TPW Sync Types
export interface TpwSyncStatus {
  attempted: boolean;
  success: boolean;
  error?: string;
}

export interface DeviceUpdateResponse {
  success?: boolean;
  tpwSync?: TpwSyncStatus;
}

export const curCoreClient = new CoreClient();
